/**
 * Nudge de volumen mensual en la lista de Ventas (soft limit Cap B).
 * Solo plan gratis, owner, cuando `volume_soft_limit.near`. Nunca bloquea.
 * Mismo dismiss que SoftLimitCreceCard: `near` persiste por período,
 * `over` snoozea 1 día (reaparece a diario si sigue excedido).
 */
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { creceVolumeSoftLimitCopy } from '../../../../../shared/plan-crece-copy';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';
import { resolveSubscribedPaidTier } from '../../shell/billing/billingScreen.utils';
import { trackCreceFunnel } from '../../shell/billing/trackCreceFunnel';
import {
  dismissSoftLimit,
  isSoftLimitDismissed,
  type SoftLimitKind,
} from '../../shell/billing/softLimitDismissal';
import { hottestVolumeMetric } from '../../shell/billing/softLimitUsage';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';

const SOURCE = 'sales_list';

export function SalesVolumeSoftLimitRow() {
  const navigation = useNavigation<any>();
  const { entitlements } = usePlanEntitlements();
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const viewTrackedFor = useRef<string | null>(null);

  const vsl = entitlements?.volume_soft_limit;
  const plan = (entitlements?.plan_code ?? '').toLowerCase();
  const show =
    isBillingEnabled() &&
    entitlements != null &&
    entitlements.is_owner === true &&
    plan === 'gratis' &&
    resolveSubscribedPaidTier(entitlements) === 'gratis' &&
    vsl?.near === true;

  const detail = show ? hottestVolumeMetric(vsl?.metrics) : null;
  const over = vsl?.over === true;
  const kind: SoftLimitKind = over ? 'volume_over' : 'volume_near';
  const periodMonth = vsl?.period_month ?? '';
  const orgId = entitlements?.organization_id ?? '';
  const stateKey = show ? `${orgId}:${periodMonth}:${kind}` : null;

  useEffect(() => {
    let cancelled = false;
    if (!stateKey) {
      setDismissed(null);
      return;
    }
    setDismissed(null);
    void isSoftLimitDismissed(orgId, periodMonth, kind)
      .then((v) => {
        if (!cancelled) setDismissed(v);
      })
      .catch(() => {
        if (!cancelled) setDismissed(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  useEffect(() => {
    if (!stateKey || dismissed !== false) return;
    if (viewTrackedFor.current === stateKey) return;
    viewTrackedFor.current = stateKey;
    trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitView, {
      source: SOURCE,
      ratio: detail?.ratio ?? vsl?.ratio ?? 0,
      period_month: periodMonth,
    });
  }, [stateKey, dismissed, detail?.ratio, vsl?.ratio, periodMonth]);

  if (!stateKey || dismissed !== false) return null;

  const title = over
    ? creceVolumeSoftLimitCopy.titleOver
    : detail != null && detail.remaining > 0
      ? creceVolumeSoftLimitCopy.titleNearRemaining(
          detail.remaining,
          detail.singular,
          detail.plural,
        )
      : creceVolumeSoftLimitCopy.titleNear;

  return (
    <View style={styles.row} testID="sales.volume-soft-limit">
      <View style={styles.textWrap}>
        <Text style={styles.title}>{title}</Text>
        {detail?.meta ? <Text style={styles.meta}>{detail.meta}</Text> : null}
      </View>
      <TouchableOpacity
        style={styles.cta}
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, { source: SOURCE });
          navigation.navigate('ActivateCrece' as never);
        }}
      >
        <Text style={styles.ctaText}>Ver plan Crece</Text>
      </TouchableOpacity>
      <TouchableOpacity
        accessibilityLabel={creceVolumeSoftLimitCopy.dismiss}
        hitSlop={8}
        onPress={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.softLimitDismiss, {
            source: SOURCE,
            ratio: detail?.ratio ?? vsl?.ratio ?? 0,
            period_month: periodMonth,
          });
          void dismissSoftLimit(orgId, periodMonth, kind);
          setDismissed(true);
        }}
      >
        <Text style={styles.dismiss}>✕</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  textWrap: { flex: 1, gap: 2 },
  title: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  meta: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  cta: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.crece,
  },
  ctaText: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.crece,
  },
  dismiss: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink3,
    paddingHorizontal: spacing.xs,
  },
});
